import React, { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { ZonedDateTime } from "@js-joda/core";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { MdmObjectDto, MdmObjectJobDto, MdmObjectJobDtoInput, StatusType, useCreateJobMutation, useDeleteJobMutation, useGetJobsByObjectIdQuery, useUpdateJobMutation } from "gql/generated/types";
import { GET_JOBS_BY_OBJECT_ID } from "gql/job.operations";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag, TagSeverityType } from "primereact/tag";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { TableColumn } from "utils/types";
import { z } from "zod";
import { errorToaster, getFormErrorMessage, successToaster } from "../utils/utils";
import ItemsTable from "./ItemsTable";

const statusType: Record<string, { value: string, severity: TagSeverityType }> = {
  TODO: { value: "To Do", severity: "info" },
  IN_PROGRESS: { value: "In Progress", severity: "warning" },
  DONE: { value: "Done", severity: "success" },
  CANCELLED: { value: "Cancelled", severity: "danger" },
}

type ObjectJobsProps = {
  object: MdmObjectDto
}

const ObjectJobs: React.FC<ObjectJobsProps> = ({ object }) => {
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState<MdmObjectJobDtoInput| null>(null);
  const { data, loading } = useGetJobsByObjectIdQuery({
    variables: {
      id: object.id!
    },
    skip: !object.id
  });

  const [deleteJobMutation] = useDeleteJobMutation({
    onCompleted: () => successToaster("Job deleted successfully"),
    onError: errorToaster,
  });

  const renderTag = (rowData: MdmObjectJobDto) => <Tag value={statusType[rowData?.status!].value} severity={statusType[rowData?.status!].severity} />


  const statusRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={Object.entries(statusType).map(([key, value]) => ({ label: value.value, value: key }))}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Select One"
        showClear
      />
    );
  };


  const renderActions = (rowData: MdmObjectJobDto) => (
    <>
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text h-2rem w-2rem"
        onClick={() => handleEditClick(rowData)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text p-button-danger h-2rem w-2rem"
        onClick={e => handleDeleteJob(rowData, e)}
      />
    </>
  );

  const handleEditClick = (rowData: MdmObjectJobDto) => {
    setJob(rowData);
    setOpen(true);
  }

  const handleDeleteJob = (jobData: MdmObjectJobDto, event: React.MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: event?.currentTarget,
      message: "Are you sure you want to delete this job?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: 'p-button-danger',
      accept: () => {
        deleteJobMutation({
          variables: {
            jobId: jobData.id,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: jobData.id, __typename: "MdmObjectJobDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
          },
        });
      },
    });
  };

  const columns: TableColumn<MdmObjectJobDtoInput>[] = [
    {
      field: "status",
      header: "Status",
      body: renderTag,
      sortable: true,
      filter: true,
      filterElement: statusRowFilterTemplate,
      showFilterMatchModes: false,
    },
    {
      field: "description",
      header: "Description",
      style: { width: "32rem" },
    },
    { field: "userName", header: "User Name", filter: true },
    {
      field: "startDate",
      header: "Date of Start",
      body: rowData => ZonedDateTime.parse(rowData?.startDate).toLocalDate().toString(),
      sortable: true
    },
    {
      field: "endDate",
      header: "Date of End",
      body: rowData => ZonedDateTime.parse(rowData?.endDate).toLocalDate().toString(),
      sortable: true
    },
    { header: "Actions", body: renderActions },
  ]; 

  const handleCloseDialog = () => {
    setJob(null);
    setOpen(false);
  }
  
  return (
    <>
      <ConfirmPopup />
      <ItemsTable
        data={data?.allJobsByObjectId ?? []}
        isLoading={loading}
        columns={columns}
        onToolbarBtnClick={() => setOpen(true)}
        searchFields={["description", "userName", "startDate", "endDate"]}
      />
      <Dialog
        header={job ? "Edit Job" : "Create Job"}
        visible={open}
        onHide={handleCloseDialog}
        className="w-6"
      >
        <JobForm object={object} closeDialog={handleCloseDialog} job={job} />
      </Dialog>
    </>
  );
}

export default ObjectJobs;

const schema = z.object({
  status: z.string().min(1, "Required"),
  description: z.string().min(1, "Required"),
  userName: z.string().min(1, "Required"),
  startDate: z.string(),
  endDate: z.string(),
  objectId: z.string().optional(),
  id: z.number().optional(),
})

type JobFormProps = {
  object: MdmObjectDto;
  closeDialog: () => void;
  job: MdmObjectJobDtoInput | null
};


const JobForm: React.FC<JobFormProps> = ({ object, closeDialog, job }) => {
  const defaultValues: MdmObjectJobDtoInput = {
    status: StatusType.Todo,
    description: "",
    userName: "",
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    objectId: object.id
  };

  const {control, handleSubmit, formState: { errors }, reset} = useForm<MdmObjectJobDtoInput>({
    defaultValues: job ?? defaultValues,
    resolver: zodResolver(schema),
  });

  const [createJobMutation] = useCreateJobMutation({
    onCompleted: () => successToaster("Job created successfully"),
    onError: errorToaster,
  });
  
  const [updateJobMutation] = useUpdateJobMutation({
    onCompleted: () => successToaster("Job updated successfully"),
    onError: errorToaster,
  });


  const handleCreateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    createJobMutation({
      variables: {
        input: formData,
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_JOBS_BY_OBJECT_ID, variables: {id: object.id}}, (oldData) => ({ 
          allJobsByObjectId: [...oldData.allJobsByObjectId, qData?.createJob]
        }) );
      }
    });
    reset();
    closeDialog();
  };

  const handleUpdateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    updateJobMutation({
      variables: {
        input: formData,
      },
    });
    reset();
    closeDialog();
  };

  return (
    <form className="flex flex-wrap">
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Status">
            <Dropdown
              value={field.value}
              options={Object.keys(statusType)}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="userName"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="User Name">
            <InputText
              value={field.value ?? ""}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="startDate"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Start Date" col={4}>
            <Calendar
              value={new Date(field.value)}
              dateFormat="yy-mm-dd"
              onChange={e => field.onChange((e.target.value as Date).toISOString())}
            />
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="endDate"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="End Date">
            <Calendar
              value={new Date(field.value)}
              dateFormat="yy-mm-dd"
              onChange={e => field.onChange((e.target.value as Date).toISOString())}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <InputLabelWrapper labelName="Description" className="w-full">
            <InputTextarea
              value={field.value ?? ""}
              rows={4}
              maxLength={255}
              className="p-inputtext-sm"
              onChange={e => field.onChange(e.target.value)}
            />
            {getFormErrorMessage(field.name, errors)}
          </InputLabelWrapper>
        )}
      />
      {job ? (
        <Button
          label="Update"
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleUpdateJob)}
        />
      ) : (
        <Button
          label="Create"
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleCreateJob)}
        />
      )}
    </form>
  );
}